import React, { memo } from "react";
import { RCCGrid, RCCIcon } from "react-common-components";
import List from "src/components/List";
import { useTranslation } from "src/context/Translations/context";
import formatDate from "src/utils/formatDate";
import Title from "src/components/Title";
import { addYears } from "date-fns";
import { formatValue } from "src/pages/FinancialManagement/IncomeStatement/components/List/columns";
import * as ListStyles from "./styles";
import { IListIncomeValue } from "./interface";
import { IValuesTable } from "../../interface";

const ListTable: React.FC<IListIncomeValue> = ({
	data,
	subsidiary,
	openChartIndicators,
	coinPlannedInitials,
	coinRealizedInitials
}) => {
	const { translate, currentLanguage } = useTranslation();

	const colTitle = (row: any) => (
		<ListStyles.DivAlignCenterWithOverflow mediaQueryMaxWidth>
			{row.indicatorId !== null && (
				<ListStyles.StyledDivIconIndicatorChart>
					<RCCIcon 
						name="chartBar"
						color="var(--default-color-font)"
						onClick={() => {
							if(!openChartIndicators)
								return;
							openChartIndicators(row.indicatorId, row.indicatorDescription);
						}}
					/>
				</ListStyles.StyledDivIconIndicatorChart>
			)}
			<ListStyles.OverflowSpan title={row.description} bold={row.highlightLine}> 
				{row.description} 
			</ListStyles.OverflowSpan>
		</ListStyles.DivAlignCenterWithOverflow>
	);

	const customHeaderColTitle = () => (<ListStyles.DivAlignCenterWithOverflow mediaQueryMaxWidth directionColumn>
		<ListStyles.OverflowSpan bold>{translate("Estrutura")}</ListStyles.OverflowSpan>
		<ListStyles.OverflowSpan bold purple style={{fontSize: "0.8rem"}}>
			{translate("Unidade/Filial")}: {subsidiary || translate("Total")}
		</ListStyles.OverflowSpan>
	</ListStyles.DivAlignCenterWithOverflow>);

	const colValues = (row: any) => (
		<ListStyles.DivValues borderRight>
			<RCCGrid 
				container
				item
				styleInline="flex-wrap: unset !important;"
			>
				<ListStyles.DivFlex>
					<ListStyles.TD className="value-realized break-columns-in-csv-here">
						<ListStyles.OverflowSpan 
							red={row.values[0].realized < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(row.values[0].quantityRealized, currentLanguage, 0) : formatValue(row.values[0].realized, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>

					<ListStyles.TD className="value-realized break-columns-in-csv-here">
						<ListStyles.OverflowSpan 
							red={row.values[1].realized < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(row.values[1].quantityRealized, currentLanguage, 0) : formatValue(row.values[1].realized, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>

					<ListStyles.TD className="value-predicted break-columns-in-csv-here">
						<ListStyles.OverflowSpan 
							red={row.values[1].predicted < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(row.values[1].quantityPredicted, currentLanguage, 0) : formatValue(row.values[1].predicted, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>
					
					<ListStyles.TD className="value-realized break-columns-in-csv-here">
						<ListStyles.OverflowSpan 
							red={row.values[1].lastYear < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(row.values[1].quantityLastYear, currentLanguage, 0) : formatValue(row.values[1].lastYear, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>

				</ListStyles.DivFlex>
			</RCCGrid>
		</ListStyles.DivValues>
	);

	const customHeaderColValues = (rows: any) => {
		const firstRow = rows[0];
		const currentMonth = formatDate(firstRow.values[1].date, currentLanguage, false, "dd/");
		const lastMonth = formatDate(firstRow.values[0].date, currentLanguage, false, "dd/");
		const lastYearDate = formatDate(addYears(new Date(firstRow.values[1].date), -1), currentLanguage, false, "dd/");
		return(
			<ListStyles.DivValues borderRight>
				<RCCGrid 
					container
					item
					styleInline="flex-wrap: unset !important;"
				>
					<ListStyles.TD>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								{translate("Realizado")}
							</Title>
							{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{lastMonth}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>

					<ListStyles.TD>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								{translate("Realizado")}
							</Title>
							{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{currentMonth}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>

					<ListStyles.TD>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								{translate("Planejado")}
							</Title>
							{coinPlannedInitials ? <small>({coinPlannedInitials})</small> : ""}
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{currentMonth}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>

					<ListStyles.TD>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								{translate("Realizado")}
							</Title>
							{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{lastYearDate}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>

				</RCCGrid>
			</ListStyles.DivValues>
		);};

	const colAccumulatedValues = (row: any) => {
		const valuesAccumulatedUntilDate = row.values.find((x: IValuesTable) => x.performanceValueIdentifier === "valuesAccumulatedUntilDate");
		const valuesAccumulatedUntilDateLastYear = row.values.find((x: IValuesTable) => x.performanceValueIdentifier === "valuesAccumulatedUntilDateLastYear");
		const valuesAccumulatedDateYear = row.values.find((x: IValuesTable) => x.performanceValueIdentifier === "valuesAccumulatedDateYear");
		const valuesAccumulatedDateLastYear = row.values.find((x: IValuesTable) => x.performanceValueIdentifier === "valuesAccumulatedDateLastYear");
		return(
			<ListStyles.DivValues borderLeft>
				<RCCGrid 
					container
					item
					styleInline="flex-wrap: unset !important;"
				>

					<ListStyles.TD className="value-realized break-columns-in-csv-here">
						<ListStyles.OverflowSpan 
							red={valuesAccumulatedUntilDate.realized < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(valuesAccumulatedUntilDate.quantityRealized, currentLanguage, 0) : formatValue(valuesAccumulatedUntilDate.realized, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>

					<ListStyles.TD className="value-predicted break-columns-in-csv-here">
						<ListStyles.OverflowSpan 
							red={valuesAccumulatedUntilDate.predicted < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(valuesAccumulatedUntilDate.quantityPredicted, currentLanguage, 0) : formatValue(valuesAccumulatedUntilDate.predicted, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>
						
					<ListStyles.TD className="value-realized break-columns-in-csv-here">
						<ListStyles.OverflowSpan 
							red={valuesAccumulatedUntilDateLastYear.realized < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(valuesAccumulatedUntilDateLastYear.quantityRealized, currentLanguage, 0) : formatValue(valuesAccumulatedUntilDateLastYear.realized, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>

					<ListStyles.TD className="value-realized break-columns-in-csv-here" borderLeft>
						<ListStyles.OverflowSpan 
							red={valuesAccumulatedDateYear.forecast < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(valuesAccumulatedDateYear.quantityForecast, currentLanguage, 0) : formatValue(valuesAccumulatedDateYear.forecast, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>

					<ListStyles.TD className="value-predicted break-columns-in-csv-here">
						<ListStyles.OverflowSpan 
							red={valuesAccumulatedDateYear.predicted < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(valuesAccumulatedDateYear.quantityPredicted, currentLanguage, 0) : formatValue(valuesAccumulatedDateYear.predicted, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>
						
					<ListStyles.TD className="value-realized break-columns-in-csv-here">
						<ListStyles.OverflowSpan 
							red={valuesAccumulatedDateLastYear.realized < 0}
							bold={row.highlightLine}
						>
							{row.format === "Q" ? formatValue(valuesAccumulatedDateLastYear.quantityRealized, currentLanguage, 0) : formatValue(valuesAccumulatedDateLastYear.realized, currentLanguage, row.format === "P" ? 1 : 0, row.format === "P")}
						</ListStyles.OverflowSpan>
					</ListStyles.TD>
				</RCCGrid>
			</ListStyles.DivValues>
		);};

	const customHeaderColAccumulatedValues = (rows: any) => {
		const firstRow = rows[0];
		const currentMonth = formatDate(firstRow.values[1].date, currentLanguage, false, "dd/");
		const currentMonthLastYear = formatDate(addYears(new Date(firstRow.values[1].date), -1), currentLanguage, false, "dd/");
		const currentYear = new Date(firstRow.values[1].date).getFullYear();
		const lastYear = currentYear - 1;
		return(
			<ListStyles.DivValues borderLeft>
				<RCCGrid 
					container
					item
					styleInline="flex-wrap: unset !important;"
				>
					<ListStyles.TD>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								&nbsp;
							</Title>
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{currentMonth}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>
	
					<ListStyles.TD>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								{translate("Acumulado")}
							</Title>
							{coinPlannedInitials ? <small>({coinPlannedInitials})</small> : ""}
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{translate("Planejado")}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>
	
					<ListStyles.TD>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								&nbsp;
							</Title>
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{currentMonthLastYear}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>
	
					<ListStyles.TD borderLeft>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								{translate("Forecast")}
							</Title>
							{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{currentYear}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>

					<ListStyles.TD>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								{translate("Planejado")}
							</Title>
							{coinPlannedInitials ? <small>({coinPlannedInitials})</small> : ""}
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{currentYear}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>

					<ListStyles.TD>
						<ListStyles.DivColumn className="break-columns-in-csv-here">
							<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
								{translate("Realizado")}
							</Title>
							{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
							<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
								{lastYear}
							</Title>
						</ListStyles.DivColumn>
					</ListStyles.TD>
	
				</RCCGrid>
			</ListStyles.DivValues>
		);};

	return(
		<div style={{overflowX: "auto"}}>
			<List 
				data={data}
				columns={[{
					idColumn: "values",
					nameColumn: translate("Valores"),
					cellRenderer: colValues,
					customHeader: customHeaderColValues,
					styleInline: "width: 392px !important; background: var(--li-child-color);"
				},{
					idColumn: "title",
					nameColumn: translate("Indicador"),
					justify: "left",
					cellRenderer: colTitle,
					customHeader: customHeaderColTitle,
					styleInline: "flex: 1;"
				},
				{
					idColumn: "accumulatedValues",
					nameColumn: translate("Acumulado"),
					cellRenderer: colAccumulatedValues,
					customHeader: customHeaderColAccumulatedValues,
					styleInline: "width: 592px !important; background: var(--li-child-color);"
				}]}
				showHeader
				headerHiddenXs={false}
				layout="table"
				defaultNumberOfElements={200}
				manuallyToggleShowMore
				styleInline={ListStyles.ListStyles}
			/>
		</div>
	);};

export default memo(ListTable);
